<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Back nav -->
    <div class="bg-white border-b border-gray-100 sticky top-0 z-10 no-print">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 py-3 tw-flex items-center justify-between gap-4">
        <router-link
          to="/profile/update#personal-home"
          class="tw-flex items-center gap-1.5 text-sm text-gray-500 no-underline hover:text-gray-800 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          My Foodleap
        </router-link>
        <div class="tw-flex items-center gap-2">
          <button
            v-if="isOwner"
            @click="openShareModal"
            class="tw-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
            </svg>
            {{ $t('trp_share') }}
          </button>
          <button
            @click="exportPDF"
            class="tw-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            Export PDF
          </button>
        </div>
      </div>
    </div>

    <TransitionPulseShareModal
      v-if="showShareModal"
      :report-id="id"
      @close="showShareModal = false"
      @updated="sharedUsers = $event"
    />

    <!-- Loading -->
    <div v-if="isLoading" class="max-w-4xl mx-auto px-4 sm:px-6 py-16 tw-flex justify-center">
      <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>

    <div v-else-if="!pulseData" class="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center text-gray-400">
      No Pulse found for this period.
    </div>

    <div v-else class="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

      <!-- Header -->
      <div class="tw-flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-gray-900 m-0">Pulse</h1>
          <!-- Shared by / shared with avatars -->
          <component
            :is="isOwner ? 'button' : 'div'"
            v-if="sharePeople.length > 0 && (!isOwner || sharedUsers.length > 0)"
            class="no-print tw-flex items-center gap-2 mt-2 bg-transparent border-0 p-0"
            :class="isOwner ? 'cursor-pointer group' : ''"
            @click="isOwner && openShareModal()"
          >
            <span class="text-xs text-gray-500" :class="isOwner ? 'group-hover:text-gray-800 transition-colors' : ''">
              {{ shareLabel }}
            </span>
            <div class="tw-flex -space-x-2">
              <div
                v-for="person in visibleSharePeople"
                :key="person.id"
                class="share-avatar w-6 h-6 rounded-full bg-primary-100 ring-2 ring-gray-50 tw-flex items-center justify-center"
                :data-name="person.name"
              >
                <img v-if="person.photo_url" :src="person.photo_url" :alt="person.name" class="w-full h-full object-cover rounded-full"/>
                <span v-else class="text-[9px] font-medium text-primary-700">{{ initials(person.name) }}</span>
              </div>
              <div
                v-if="sharePeople.length > visibleSharePeople.length"
                class="share-avatar w-6 h-6 rounded-full bg-gray-200 ring-2 ring-gray-50 tw-flex items-center justify-center"
                :data-name="hiddenSharePeopleNames"
              >
                <span class="text-[9px] font-medium text-gray-600">+{{ sharePeople.length - visibleSharePeople.length }}</span>
              </div>
            </div>
          </component>
        </div>
        <select
          v-if="pulseMonths.length > 0"
          class="no-print text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none"
          @change="onPeriodChange($event.target.value)"
          :aria-label="$t('trp_period_select')"
        >
          <option
            v-for="m in pulseMonths"
            :key="m.id"
            :value="m.id"
            :selected="m.id === id"
          >
            {{ pulseMonthLabel(m.year, m.month) }}
          </option>
        </select>
        <p v-else class="text-xs font-semibold uppercase tracking-widest text-primary m-0">{{ monthLabel }}</p>
      </div>

      <!-- Category tabs -->
      <div class="no-print tw-flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
        <button
          v-for="cat in categories"
          :key="cat.key"
          @click="activeCategory = cat.key; expandedSignals = {}"
          :class="[
            'flex-1 max-w-[250px] min-w-[80px] px-3 py-2 rounded-lg text-sm font-medium transition-all',
            activeCategory === cat.key
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          {{ cat.label }}
        </button>
      </div>

      <template v-if="activeData">

        <!-- Domain title -->
        <!--        <h2 class="text-xl font-bold text-gray-900 m-0">{{ activeData.title }}</h2>-->

        <!-- 1. Key Judgment -->
        <div class="space-y-1">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 m-0">What happened this {{ periodLabel }}</p>
          <p class="text-sm text-gray-700 leading-relaxed m-0 whitespace-pre-line">{{ activeData.key_judgment }}</p>
        </div>

        <!-- 2. Why This Period Matters -->
        <div class="space-y-1">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 m-0">Why this {{ periodLabel }} matters</p>
          <p class="text-sm text-gray-700 leading-relaxed m-0 whitespace-pre-line">{{ activeData.why_this_matters }}</p>
        </div>

        <!-- 3. What is driving the transition now — 4 blocks -->
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-4 m-0">What is driving the transition now</p>
          <div class="grid gap-4 sm:grid-cols-2">
            <div
              v-for="group in drivingGroups"
              :key="group.key"
              class="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
            >
              <p class="text-xs font-semibold text-primary mb-3 m-0">{{ group.label }}</p>
              <div class="space-y-3">
                <div
                  v-for="(signal, i) in activeData.driving_signals[group.key]"
                  :key="i"
                  class="rounded-lg"
                  :class="{ 'bg-gray-50': expandedSignals[group.key + '_' + i] }"
                >
                  <!-- Signal row -->
                  <div
                    class="tw-flex items-start gap-1.5 rounded-lg px-2 py-1.5 -mx-2 transition-colors"
                    :class="signal.articles && signal.articles.filter(a => a.url).length ? 'cursor-pointer hover:bg-gray-50' : ''"
                    @click="signal.articles && signal.articles.filter(a => a.url).length && toggleSignal(group.key, i)"
                  >
                    <span :class="['text-sm font-bold shrink-0 mt-px m-auto justify-center self-center text-center mr-1', trendColorClass(signal.trend)]" style="width: 25px;">
                      {{ trendArrow(signal.trend) }}
                    </span>
                    <div class="flex-1 min-w-0">
                      <span class="text-sm font-semibold text-gray-900 leading-snug">{{ signal.name }}</span>
                      <p v-if="signal.meta" class="text-xs text-gray-400 m-0 mt-0.5">{{ signal.meta }}</p>
                    </div>
                    <svg
                      v-if="signal.articles && signal.articles.filter(a => a.url).length"
                      class="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1 transition-transform duration-200"
                      :class="{ 'rotate-180': expandedSignals[group.key + '_' + i] }"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>

                  <!-- Articles (collapsible) -->
                  <div
                    v-if="signal.articles && signal.articles.filter(a => a.url).length && expandedSignals[group.key + '_' + i]"
                    class="mt-1 pb-2 px-2 space-y-1"
                  >
                    <a
                      v-for="article in signal.articles.filter(a => a.url)"
                      :key="article.url_id"
                      :href="article.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="tw-flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-gray-100 hover:border-gray-300 transition-colors no-underline group"
                    >
                      <svg class="w-3 h-3 text-gray-300 shrink-0 group-hover:text-gray-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      <span class="text-xs text-gray-600 leading-snug flex-1 min-w-0 truncate group-hover:text-gray-900 transition-colors">
                        {{ article.title || articleDomain(article.url) || 'Article' }}
                      </span>
                      <svg v-if="article.url" class="w-3 h-3 text-gray-300 shrink-0 group-hover:text-gray-400 transition-colors" fill="none" stroke="currentColor"
                           viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Actors in motion -->
        <div v-if="activeData.actors && activeData.actors.length">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3 m-0">Change makers in motion</p>
          <div class="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
            <component
              :is="actor.elastic_id ? 'router-link' : 'div'"
              v-for="(actor, i) in activeData.actors"
              :key="i"
              :to="actor.elastic_id ? '/actors/' + actor.elastic_id : undefined"
              class="tw-flex items-center gap-3 px-4 py-3 no-underline hover:bg-gray-50 transition-colors group"
            >
              <div class="w-1.5 h-1.5 rounded-full bg-primary shrink-0 opacity-60"/>
              <div class="min-w-0 flex-1">
                <span class="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">{{ actor.name }}</span>
                <p
                  v-if="actor.description"
                  class="text-xs text-gray-400 m-0 line-clamp-2"
                >
                  {{ actor.description }}
                </p>
              </div>
              <svg v-if="actor.elastic_id" class="w-3.5 h-3.5 text-gray-300 group-hover:text-primary transition-colors shrink-0" fill="none" stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </component>
          </div>
        </div>

      </template>

    </div>
  </div>
</template>

<script>

  import { fetchTransitionPulse, fetchTransitionPulseMonths } from '@/api/transition-pulse'
  import { fetchMyClaims } from '@/api/network-contacts'
  import TransitionPulseShareModal from '@/components/TransitionPulse/TransitionPulseShareModal.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'

  const CATEGORY_LABELS = {
    protein: 'Protein',
    health: 'Health',
    upcycling: 'Upcycling',
    circular: 'Circular',
  }

  const DRIVING_GROUPS = [
    { key: 'capital_investment', label: 'Capital & Investment' },
    { key: 'technology_innovation', label: 'Technology & Innovation' },
    { key: 'policy_regulatory', label: 'Policy & Regulatory' },
    { key: 'consumer_market', label: 'Consumer & Market' },
  ]

  export default {
    name: 'TransitionPulseDetail',

    components: { TransitionPulseShareModal },

    data () {
      return {
        isLoading: true,
        pulseData: null,
        timeframe: 'monthly',
        activeCategory: null,
        expandedSignals: {},
        pulseMonths: [],
        // year/month of the loaded report — used for the label, since the URL is now keyed by id.
        pulseYear: null,
        pulseMonth: null,
        isOwner: false,
        showShareModal: false,
        owner: null,
        sharedUsers: [],
      }
    },

    computed: {
      id () {
        return Number(this.$route.params.id)
      },
      periodLabel () {
        return this.timeframe === 'weekly' ? 'week' : 'month'
      },
      monthLabel () {
        if (!this.pulseYear || !this.pulseMonth) return ''
        return new Date(this.pulseYear, this.pulseMonth - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      },
      categories () {
        if (!this.pulseData) return []
        return Object.keys(this.pulseData)
          .filter(key => {
            const val = this.pulseData[key]
            return val && !Array.isArray(val) && Object.keys(val).length > 0
          })
          .map(key => ({
            key,
            label: (key.charAt(0).toUpperCase() + key.slice(1)).replaceAll('_', ' '),
          }))
      },
      activeData () {
        if (!this.pulseData || !this.activeCategory) return null
        const d = this.pulseData[this.activeCategory]
        return Array.isArray(d) ? d[0] : d
      },
      drivingGroups () {
        return DRIVING_GROUPS
      },
      // Owner first, then the users the report is shared with.
      sharePeople () {
        const seen = new Set()
        return [this.owner, ...this.sharedUsers].filter(p => {
          if (!p?.id || seen.has(p.id)) return false
          seen.add(p.id)
          return true
        })
      },
      visibleSharePeople () {
        return this.sharePeople.slice(0, 5)
      },
      hiddenSharePeopleNames () {
        return this.sharePeople.slice(5).map(p => p.name).join(', ')
      },
      shareLabel () {
        if (this.isOwner) return this.$t('trp_shared_with')
        return `${this.$t('trp_shared_by')} ${this.owner?.name || ''}`
      },
    },

    methods: {
      // Ecosystem members need a claimed actor before they can see/invite its contributors
      async openShareModal () {
        if (this.$store.getters.isActor) {
          const claims = await fetchMyClaims().catch(() => [])

          if (!claims?.length) {
            this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actorRequiredVariant: 'share' })
            this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ACTOR_CLAIM_REQUIRED)
            return
          }
        }

        this.showShareModal = true
      },
      initials (name) {
        return name
          ?.split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2) || '?'
      },
      toggleSignal (groupKey, index) {
        const key = groupKey + '_' + index
        this.expandedSignals = { ...this.expandedSignals, [key]: !this.expandedSignals[key] }
      },
      articleDomain (url) {
        if (!url) return null
        try {
          return new URL(url).hostname.replace(/^www\./, '')
        } catch {
          return null
        }
      },
      trendArrow (trend) {
        const map = { 'double-up': '↑↑', up: '↑', neutral: '→', down: '↓', 'double-down': '↓↓' }
        return map[trend] || '→'
      },
      trendColorClass (trend) {
        if (trend === 'up' || trend === 'double-up') return 'text-green-600'
        if (trend === 'down' || trend === 'double-down') return 'text-red-500'
        return 'text-gray-400'
      },
      async exportPDF () {
        const html2pdf = (await import('html2pdf.js')).default
        await html2pdf().set({
          margin: [12, 14],
          filename: `transition-pulse-${this.monthLabel.replace(/\s/g, '-').toLowerCase()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        }).from(this.buildReportHTML(), 'string').save()
      },
      buildReportHTML () {
        const GROUP_LABELS = {
          capital_investment: 'Capital & Investment',
          technology_innovation: 'Technology & Innovation',
          policy_regulatory: 'Policy & Regulatory',
          consumer_market: 'Consumer & Market',
        }
        const arrow = (t) => ({ 'double-up': '↑↑', up: '↑', neutral: '→', down: '↓', 'double-down': '↓↓' }[t] || '→')
        const arrowColor = (t) => (t === 'up' || t === 'double-up') ? '#16a34a' : (t === 'down' || t === 'double-down') ? '#dc2626' : '#9ca3af'
        const periodTitle = this.timeframe === 'weekly' ? 'Week' : 'Month'

        const categorySections = Object.entries(this.pulseData || {}).map(([, cat]) => {
          const signalGroups = Object.entries(cat.driving_signals || {}).map(([key, signals]) => `
            <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px;">
              <div style="font-size:11px;font-weight:700;color:#15803d;margin-bottom:10px;text-transform:uppercase;letter-spacing:0.05em;">${GROUP_LABELS[key] || key}</div>
              ${(signals || []).map(s => `
                <div style="margin-bottom:8px;">
                  <span style="font-weight:700;font-size:13px;color:${arrowColor(s.trend)};margin-right:4px;">${arrow(s.trend)}</span>
                  <span style="font-size:13px;font-weight:600;color:#111827;">${s.name}</span>
                  ${s.meta ? `<div style="font-size:11px;color:#9ca3af;padding-left:18px;margin-top:1px;">${s.meta}</div>` : ''}
                </div>`).join('')}
            </div>`).join('')

          const actors = (cat.actors || []).map(a =>
            `<div style="font-size:13px;padding:5px 0;border-bottom:1px solid #f3f4f6;">
              <span style="font-weight:600;">${a.name}</span>
              ${a.meta ? ` — <span style="color:#6b7280;">${a.meta}</span>` : ''}
            </div>`
          ).join('')

          return `
            <div style="margin-bottom:40px;page-break-after:always;">
              <h2 style="font-size:20px;font-weight:700;margin-bottom:20px;">${cat.title || ''}</h2>
              <div style="margin-bottom:18px;">
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Key Judgment</div>
                <p style="font-size:13px;color:#374151;line-height:1.7;white-space:pre-line;">${cat.key_judgment || ''}</p>
              </div>
              <div style="margin-bottom:18px;">
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Why This ${periodTitle} Matters</div>
                <p style="font-size:13px;color:#374151;line-height:1.7;white-space:pre-line;">${cat.why_this_matters || ''}</p>
              </div>
              <div style="margin-bottom:18px;">
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:8px;">What is driving the transition now</div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;">${signalGroups}</div>
              </div>
              ${actors ? `<div>
                <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Change makers in motion</div>
                ${actors}
              </div>` : ''}
            </div>`
        }).join('')

        return `<div style="width:780px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1f2937;line-height:1.6;background:#fff;padding:32px 28px 60px;">
          <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #1f2937;padding-bottom:12px;margin-bottom:32px;">
            <div style="font-size:22px;font-weight:700;">Pulse</div>
            <div style="font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;">${this.monthLabel}</div>
          </div>
          ${categorySections}
        </div>`
      },
      pulseMonthLabel (year, month) {
        return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      },
      async loadPulseMonths () {
        try {
          this.pulseMonths = await fetchTransitionPulseMonths()
        } catch (e) { /* silently ignore */
        }
      },
      onPeriodChange (value) {
        this.$router.push(`/transition-pulse/${value}`)
      },
      async loadPulse () {
        this.isLoading = true
        try {
          const response = await fetchTransitionPulse(this.id)
          this.pulseData = response?.report_content?.categories
          this.timeframe = response?.timeframe || 'monthly'
          this.pulseYear = response?.year ?? null
          this.pulseMonth = response?.month ?? null
          this.isOwner = response?.is_owner === true
          this.owner = response?.owner || null
          this.sharedUsers = response?.shared_users || []
          if (this.categories.length) this.activeCategory = this.categories[0].key
        } catch (e) {
          this.pulseData = null
          this.isOwner = false
          this.owner = null
          this.sharedUsers = []
        } finally {
          this.isLoading = false
        }
      },
    },

    watch: {
      '$route.params': {
        handler () {
          this.loadPulse()
        },
        immediate: false,
      },
    },

    async mounted () {
      await this.loadPulse()
      this.loadPulseMonths()
    },
  }
</script>

<style scoped>
  /* Instant name tooltip on the shared-with avatars (native title is too slow) */
  .share-avatar {
    position: relative;
  }

  .share-avatar:hover {
    z-index: 10;
  }

  .share-avatar::after {
    content: attr(data-name);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: #1f2937;
    color: #fff;
    font-size: 11px;
    line-height: 1;
    padding: 5px 8px;
    border-radius: 6px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }

  .share-avatar:hover::after {
    opacity: 1;
  }
</style>
